// components/calendar/CalendarDayView.js
// Vista giornaliera stile Teamleader con griglia oraria dettagliata

import React, { useMemo } from 'react';
import { format, isSameDay } from 'date-fns';
import { it } from 'date-fns/locale';
import { isItalianHoliday, isSunday, createHolidayMap, formatDateKey } from '../../utils/italianHolidays';
import { buildCalendarEventTitle, getAppointmentVenue } from './calendarEventFormatter';

// Orari visualizzati (6:00 - 24:00)
const HOURS = Array.from({ length: 19 }, (_, i) => i + 6);
const HOUR_HEIGHT = 72; // px per ora - più grande per la vista giorno

// Colori per tipo appuntamento
const APPOINTMENT_COLORS = {
  sopralluogo: 'bg-[#4A90A4] border-[#3A7A94] text-white',
  sede: 'bg-[#5B9BD5] border-[#4A8AC4] text-white',
  presentazione: 'bg-[#5B9BD5] border-[#4A8AC4] text-white',
  scelta_extra: 'bg-[#70AD47] border-[#5F9C36] text-white',
  personale: 'bg-[#7F7F7F] border-[#6F6F6F] text-white',
  interno: 'bg-[#7F7F7F] border-[#6F6F6F] text-white',
  google_external: 'bg-[#DB4437] border-[#C33326] text-white',
  annullato: 'bg-[#D9D9D9] border-[#C9C9C9] text-[#737373] line-through',
};

const CalendarDayView = ({
  currentDate,
  appointments,
  leads,
  onEventClick,
  onTimeSlotClick,
}) => {
  // Mappa festività
  const holidayMap = useMemo(() => {
    const year = currentDate.getFullYear();
    return createHolidayMap(year);
  }, [currentDate]);

  // Ottieni appuntamenti per la data corrente
  const dayAppointments = appointments.filter(apt => 
    isSameDay(new Date(apt.appointment_date), currentDate) &&
    apt.status !== 'annullato'
  ).sort((a, b) => new Date(a.appointment_date) - new Date(b.appointment_date));

  // Ottieni info lead
  const getLeadInfo = (leadId) => {
    return leads?.find(l => l.id === leadId);
  };

  // Costruisci titolo evento (utility condivisa: venue snapshot-aware)
  const buildEventTitle = (apt) => buildCalendarEventTitle(apt, getLeadInfo(apt.lead_id));

  // Calcola posizione e altezza evento
  const getEventStyle = (apt) => {
    const aptDate = new Date(apt.appointment_date);
    const hour = aptDate.getHours();
    const minutes = aptDate.getMinutes();
    const duration = apt.duration_minutes || 60;
    
    const topOffset = ((hour - 6) * 60 + minutes) / 60;
    const heightUnits = duration / 60;
    
    return {
      top: `${topOffset * HOUR_HEIGHT}px`,
      height: `${Math.max(heightUnits * HOUR_HEIGHT, 36)}px`,
      minHeight: '36px',
    };
  };

  // Ottieni colore evento
  const getEventColor = (apt) => {
    if (apt.status === 'annullato') return APPOINTMENT_COLORS.annullato;
    if (apt.source === 'google' || apt.is_google_event || apt.appointment_type === 'google_external') {
      return APPOINTMENT_COLORS.google_external;
    }
    return APPOINTMENT_COLORS[apt.appointment_type] || APPOINTMENT_COLORS.personale;
  };

  // Info giorno
  const dateKey = formatDateKey(currentDate);
  const holiday = holidayMap[dateKey];
  const sunday = isSunday(currentDate);
  const isToday = isSameDay(currentDate, new Date());

  return (
    <div className="bg-white rounded-lg border border-[#DCDCDC] overflow-hidden">
      {/* Header con data */}
      <div className={`p-4 border-b border-[#DCDCDC] ${isToday ? 'bg-[#FF7819]/10' : 'bg-[#FAFAFA]'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h3 className={`text-xl font-bold ${sunday ? 'text-red-500' : isToday ? 'text-[#FF7819]' : 'text-[#3C3C3C]'}`}>
              {format(currentDate, 'EEEE d MMMM yyyy', { locale: it })}
            </h3>
            {isToday && (
              <span className="px-3 py-1 bg-[#FF7819] text-white text-xs font-bold rounded-full">
                OGGI
              </span>
            )}
            {holiday && (
              <span className="text-sm text-red-500 font-medium">⭐ {holiday.name}</span>
            )}
          </div>
          <div className="text-sm font-medium text-[#737373] bg-[#F5F5F5] px-3 py-1 rounded-full">
            {dayAppointments.length} appuntament{dayAppointments.length === 1 ? 'o' : 'i'}
          </div>
        </div>
      </div>

      {/* Griglia oraria */}
      <div className="relative overflow-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
        <div className="grid grid-cols-[90px_1fr]">
          {HOURS.map((hour) => (
            <React.Fragment key={hour}>
              {/* Colonna ora */}
              <div 
                className="border-b border-r border-[#E5E5E5] flex items-start justify-end pr-3 pt-1 bg-[#FAFAFA]"
                style={{ height: `${HOUR_HEIGHT}px` }}
              >
                <span className="text-sm font-semibold text-[#737373]">
                  {hour.toString().padStart(2, '0')}:00
                </span>
              </div>
              
              {/* Cella giorno */}
              <div
                className={`border-b border-[#E5E5E5] relative cursor-pointer hover:bg-[#FFF8F0] transition-colors ${
                  isToday ? 'bg-[#FF7819]/5' : ''
                }`}
                style={{ height: `${HOUR_HEIGHT}px` }}
                onClick={() => onTimeSlotClick?.(currentDate, hour)}
                data-testid={`day-slot-${hour}`}
              >
                {/* Linee per quarti d'ora */}
                <div className="absolute top-1/4 left-0 right-0 border-t border-dashed border-[#F0F0F0]" />
                <div className="absolute top-1/2 left-0 right-0 border-t border-dashed border-[#E5E5E5]" />
                <div className="absolute top-3/4 left-0 right-0 border-t border-dashed border-[#F0F0F0]" />
              </div>
            </React.Fragment>
          ))}
        </div>

        {/* Eventi sovrapposti alla griglia */}
        <div 
          className="absolute top-0 left-[90px] right-4 pointer-events-none"
          style={{ height: `${HOURS.length * HOUR_HEIGHT}px` }}
        >
          {dayAppointments.map((apt, index) => {
            const style = getEventStyle(apt);
            const colorClass = getEventColor(apt);
            const title = buildEventTitle(apt);
            const aptTime = format(new Date(apt.appointment_date), 'HH:mm');
            const lead = apt.lead_id ? getLeadInfo(apt.lead_id) : null;
            
            return (
              <div
                key={apt.id || index}
                className={`absolute left-2 right-2 rounded-lg border-l-4 px-4 py-2 overflow-hidden cursor-pointer pointer-events-auto hover:opacity-90 hover:shadow-lg transition-all shadow-md ${colorClass}`}
                style={style}
                onClick={(e) => {
                  e.stopPropagation();
                  onEventClick?.(apt);
                }}
                title={title}
                data-testid={`day-event-${apt.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm flex items-center gap-2">
                      <span className="bg-white/20 px-2 py-0.5 rounded text-sm font-semibold">
                        {aptTime}
                      </span>
                      {apt.source === 'google' && (
                        <span className="inline-block w-4 h-4 flex-shrink-0">
                          <svg viewBox="0 0 24 24" className="w-full h-full">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        </span>
                      )}
                    </div>
                    <div className="font-semibold text-base mt-1 truncate">
                      {title}
                    </div>
                    {parseInt(style.height) > 90 && (() => {
                      const venue = getAppointmentVenue(apt, lead);
                      const loc = venue ? [venue.name, venue.city].filter(Boolean).join(' · ') : '';
                      return loc ? (
                        <div className="text-sm opacity-90 mt-1 truncate">
                          📍 {loc}
                        </div>
                      ) : null;
                    })()}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CalendarDayView;
